//"use client"

import { useEffect, useState, useCallback, memo } from "react"
import { Helmet } from "react-helmet"
import axiosInstance from "../../api/axiosInstance"
import { getPreloadedData, PreloadedScript } from "../../utils/preloadedData"
import "./../Faqs/Faqs.css"
import faqicon from "../../../Assets/__after.png"
import plusIcon from "../../../Assets/plus.png"
import minusIcon from "../../../Assets/minus.png"
import { IoSettingsOutline } from "react-icons/io5"

const Faqs = memo(({ scrollToAppointment, isFaqPage = false }) => {
  const [faqs, setFaqs] = useState(() => getPreloadedData("__PRELOADED_FAQS__") || [])
  const [activeIndex, setActiveIndex] = useState(null)

  const toggleFaq = useCallback(
    (index) => {
      setActiveIndex(activeIndex === index ? null : index)
    },
    [activeIndex],
  )

  useEffect(() => {
    axiosInstance
      .get("/api/faqs")
      .then((response) => {
        setFaqs(response.data)
      })
      .catch((error) => {
        console.error("There was an error fetching the FAQs!", error)
      })
  }, [])

  return (
    <div className="main_div_faqs" role="region" aria-label="Frequently Asked Questions Section">
      <PreloadedScript id="__PRELOADED_FAQS__" data={faqs} />
      {isFaqPage && (
        <Helmet>
          <title>Frequently Asked Questions | 360 SynergyTech</title>
          <meta
            name="description"
            content="Find answers to common questions about 360 SynergyTech's web development, AI, cloud, RPA, and custom CRM solutions."
          />
          <link rel="canonical" href="https://360synergytech.com/faqs" />

          <meta property="og:title" content="Frequently Asked Questions | 360 SynergyTech" />
          <meta property="og:description" content="Find answers to common questions about 360 SynergyTech's web development, AI, cloud, RPA, and custom CRM solutions." />
          <meta property="og:url" content="https://360synergytech.com/faqs" />
          <meta property="og:image" content="https://360synergytech.com/synergy-logo.png" />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Frequently Asked Questions | 360 SynergyTech" />
          <meta name="twitter:description" content="Find answers to common questions about 360 SynergyTech's web development, AI, cloud, RPA, and custom CRM solutions." />
          <meta name="twitter:image" content="https://360synergytech.com/synergy-logo.png" />

          {faqs.length > 0 && (
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: faqs.map((faq) => ({
                  "@type": "Question",
                  name: faq.question,
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: faq.answer,
                  },
                })),
              })}
            </script>
          )}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://360synergytech.com/" },
                { "@type": "ListItem", position: 2, name: "FAQs", item: "https://360synergytech.com/faqs" },
              ],
            })}
          </script>
        </Helmet>
      )}

      <div className="row p-0 m-0">
        <div className="col-12">
          <div className="row m-0 p-0">
            <div className="col-lg-6 col-md-6 col-sm-12 col-12">
              {isFaqPage ? (
                <h1 style={{ marginTop: 0, marginBottom: "1rem" }}>
                  <div className="d-flex flex-row">
                    <span className="faq_heading_white">Frequently Asked</span>
                    <span className="faq_heading_blue">Questions</span>
                  </div>
                </h1>
              ) : (
                <h2 className="d-flex flex-row" style={{ marginTop: 0, marginBottom: "1rem" }}>
                  <span className="faq_heading_white">Frequently Asked</span>
                  <span className="faq_heading_blue">Questions</span>
                </h2>
              )}
              <p className="process_heading_para" role="doc-subtitle">
                Answers to common questions about our services, process and support.
              </p>
              <button
                className="more_about_btn mb-4"
                onClick={scrollToAppointment}
                aria-label="Learn more about our services"
              >
                <IoSettingsOutline /> More About
              </button>
            </div>
            <div className="col-lg-6 col-md-6 col-sm-12 col-12">
              <div
                className="accordion"
                id="accordionExample"
                role="region"
                aria-label="FAQ Accordion"
                style={{
                  maxHeight: "500px",
                  overflowY: "scroll",
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                }}
              >
                {faqs.length > 0 ? (
                  faqs.map((faq, index) => (
                    <div
                      className={`accordion-item ${activeIndex === index ? "active" : ""}`}
                      key={index}
                      role="region"
                      aria-labelledby={`heading${index}`}
                    >
                      <h2 className="accordion-header" id={`heading${index}`}>
                        <button
                          className={`accordion-button ${activeIndex === index ? "" : "collapsed"}`}
                          type="button"
                          onClick={() => toggleFaq(index)}
                          aria-expanded={activeIndex === index ? "true" : "false"}
                          aria-controls={`collapse${index}`}
                        >
                          <img src={faqicon || "/placeholder.svg"} width="22" height="22" alt="" className="faq-icon" loading="lazy" />
                          {faq.question}
                          <img
                            src={activeIndex === index ? minusIcon : plusIcon}
                            alt={activeIndex === index ? "minus icon" : "plus icon"}
                            className="toggle-icon"
                            loading="lazy"
                          />
                        </button>
                      </h2>
                      <div
                        id={`collapse${index}`}
                        className={`accordion-collapse collapse ${activeIndex === index ? "show" : ""}`}
                        aria-labelledby={`heading${index}`}
                        data-bs-parent="#accordionExample"
                        role="region"
                      >
                        <div className="accordion-body">{faq.answer}</div>
                      </div>
                    </div>
                  ))
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
})

export default Faqs
